import { useMemo, useSyncExternalStore } from 'react';
import { getListOfVisibleSpeakers } from '../../CompositePlayer/CompositePlayerUtils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import {
  selectSpeakerAnalysis,
  selectSpeakerDetails
} from '@/player-config/src/selectors/contentMetadataStoreSelectors';
import { core } from '@/stores/core';
import { getClipBroadcastId } from '@/player-config/src/utils/clipUtils';

export const useActiveSpeakerMatchCondition = () => {
  const { clipData, layoutStatus } = useClipsContext();
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const clipBroadcastId = getClipBroadcastId(clipData);
  const speakersAnalysis = useContentMetadataStore(selectSpeakerAnalysis(clipBroadcastId ?? ''));
  const speakersWithDetails = useContentMetadataStore(selectSpeakerDetails(clipBroadcastId ?? ''));
  const speakerMapping = useMemo(() => speakersAnalysis?.speaker_mapping ?? {}, [speakersAnalysis]);

  const [isNoFaceAtEveryAnalysisPoint, isOnlyOneFaceAtEveryAnalysisPoint, isOneOrNoFaceAtEveryAnalysisPoint] = useMemo(
    () =>
      speakersAnalysis
        ? [
            speakersAnalysis.face_ids.length === 0,
            speakersAnalysis.time_analysis.every(analysisPoint => analysisPoint.face_positions.length === 1),
            speakersAnalysis.time_analysis.every(analysisPoint => analysisPoint.face_positions.length <= 1)
          ]
        : [false, false, false],
    [speakersAnalysis]
  );

  // these are the number of speakers talking in the clip
  const speakersInClip = useMemo(() => {
    return getListOfVisibleSpeakers(
      speakersWithDetails,
      clipData.asset_metadata.start,
      clipData.asset_metadata.end,
      true,
      true
    ).map(speaker => speaker.id);
  }, [speakersWithDetails, clipData.asset_metadata.start, clipData.asset_metadata.end]);

  // determines if there are unidentified speakers in the clip
  // unidentified speakers are those speakers to whom faces are not mapped
  const containsUnidentifiedSpeakers = useMemo(() => {
    if (coreStore.content?.media_source_type !== 'UPLOAD') {
      return false;
    }

    if (layoutStatus !== 'DONE') {
      return false;
    }

    const areAllSpeakersIdentified = speakersInClip.every(speakerId => speakerMapping[speakerId]?.length);

    return !areAllSpeakersIdentified;
  }, [layoutStatus, coreStore.content?.media_source_type, speakerMapping, speakersInClip]);

  // Is used to bypass the condition when there are unidentified speakers in the clip but the faces need not be mapped to speakers
  const isSpeakerIdentificationMandatory = useMemo(() => {
    if (isNoFaceAtEveryAnalysisPoint || isOnlyOneFaceAtEveryAnalysisPoint || isOneOrNoFaceAtEveryAnalysisPoint) {
      return false;
    }

    return containsUnidentifiedSpeakers;
  }, [
    isNoFaceAtEveryAnalysisPoint,
    isOnlyOneFaceAtEveryAnalysisPoint,
    isOneOrNoFaceAtEveryAnalysisPoint,
    containsUnidentifiedSpeakers
  ]);

  // used to make identify speakers behave like active speaker when using speaker grid and there is only one speaker in the clip
  const shouldBehaveLikeActiveSpeaker = useMemo(
    () => isOneOrNoFaceAtEveryAnalysisPoint && speakersInClip.length === 1,
    [isOneOrNoFaceAtEveryAnalysisPoint, speakersInClip]
  );

  return {
    isSpeakerIdentificationMandatory,
    containsUnidentifiedSpeakers,
    isNoFaceAtEveryAnalysisPoint,
    shouldBehaveLikeActiveSpeaker
  };
};
